import { useApolloClient } from "@apollo/client";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { IconButton, IconButtonProps, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useOrganisationSupplierProductFavouriteButton__PimSetSupplierProductsFavouriteMutation } from "./OrganisationSupplierProductFavouriteButton.generated.js";

interface Product {
  id: string;
  articleNumber: string;
  supplierId: string;
  organisationFavourite: boolean;
}

interface Props {
  product: Product;
  canEdit?: boolean;
  iconButtonProps?: IconButtonProps;
  refetchQueries?: string[];
}

export function OrganisationSupplierProductFavouriteButton({
  product,
  canEdit = true,
  refetchQueries,
  iconButtonProps,
}: Props) {
  const client = useApolloClient();
  const { t } = useTranslate(["Product"]);

  const [setSupplierProductsFavouritMutation, { loading }] =
    useOrganisationSupplierProductFavouriteButton__PimSetSupplierProductsFavouriteMutation(
      { client, refetchQueries }
    );

  // The tooltip's accessible label lands on the wrapper span, not the nested
  // button, so the button carries the same label as its own `aria-label`.
  const label = product.organisationFavourite
    ? t("Remove from favourites", { ns: "Product" })
    : t("Add to favourites", { ns: "Product" });

  return (
    <Tooltip title={label}>
      {/* span keeps the tooltip working while the IconButton is disabled */}
      <span>
        <IconButton
          size="small"
          color="primary"
          aria-label={label}
          {...iconButtonProps}
          onClick={
            canEdit
              ? async () => {
                  await setSupplierProductsFavouritMutation({
                    variables: {
                      input: {
                        isFavourite: !product.organisationFavourite,
                        supplierProductArticleNumbers: [
                          {
                            articleNumber: product.articleNumber,
                            supplierId: product.supplierId,
                          },
                        ],
                      },
                    },
                  });
                }
              : undefined
          }
          disabled={loading || !canEdit}
        >
          {product.organisationFavourite ? (
            <StarIcon fontSize="small" />
          ) : (
            <StarBorderIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
}
